// Standing Encore Marketing Landing Page
// Epoch 84: ICP Signal Test — $499 single-offer, 8-section layout
// PM: Gemini | Engineering: Claude Code
// Visual: SE report design system (near-black grounds, gold accent, serif display)
//   Palette: bg #0e0e0f / panels #1a1a1c / warm paper #f6f4ef / gold #d8a126 / green #5a8a52
//   Rename: product is "ICP Signal Test". Hero report card = light/anonymized. Pricing = single $499 offer.
// NOTE: all onClick handlers, anchor IDs, state, hooks, and imports preserved.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthSession } from '../hooks/useAuthSession';
import DemoRequestModal from '../components/modals/DemoRequestModal';
import ContactSalesModal from '../components/modals/ContactSalesModal';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useAuthSession();
  useDocumentTitle('Standing Encore — Market Research Test for B2B Teams');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [modalSource, setModalSource] = useState('');
  const [modalButtonId, setModalButtonId] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    if (session?.user) {
      navigate('/dashboard');
    }
  }, [session, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleBookDemo = (buttonId: string = 'default') => {
    setModalSource('/');
    setModalButtonId(buttonId);
    setIsDemoModalOpen(true);
  };

  const handleContactSales = (buttonId: string = 'default') => {
    setModalSource('/');
    setModalButtonId(buttonId);
    setIsContactModalOpen(true);
  };

  const faqs = [
    {
      question: 'How is this different from setting up a survey on SurveyMonkey or Typeform?',
      answer: 'SurveyMonkey assumes you have already solved audience access. It measures declared opinions from pre-paid panels. Standing Encore IS the reach engine. We drop conversational, peer-level inquiries directly into the inboxes of cold, inaccessible B2B professionals to track their actual revealed behavioral signals.',
    },
    {
      question: 'Are these messages sent from my company\'s domain or my team\'s personal profiles?',
      answer: 'No. All ICP Signal Test flights are managed entirely through Standing Encore\'s verified peer-research accounts by our human team. Your company domain, personal brand, and LinkedIn profile remain completely isolated from delivery and rate-limiting risks.',
    },
    {
      question: 'What happens if a discovery conversation reveals a hot sales lead?',
      answer: 'While our tests are built to extract validation data rather than push sales appointments, high-intent interest naturally occurs. The moment a cold target explicitly requests a meeting or displays buying indicators, our human CSR flags the chat and pushes the live thread directly to your unified dashboard so your team can step in.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0e0e0f]">

      {/* ── SECTION 1: NAVBAR ── */}
      <nav className={`fixed w-full z-50 transition-all duration-300 border-b ${
        isScrolled ? 'bg-[#0e0e0f]/95 backdrop-blur border-[#2c2c30]' : 'bg-transparent border-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-2.5">
              <span className="w-2 h-2 rounded-full bg-[#d8a126]"></span>
              <span className="text-lg font-semibold text-[#ECECEA] tracking-tight">Standing Encore</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#how-it-works" className="text-[#9a9a98] hover:text-[#ECECEA] text-sm font-medium transition-colors">How It Works</a>
              <a href="#the-evidence" className="text-[#9a9a98] hover:text-[#ECECEA] text-sm font-medium transition-colors">The Evidence</a>
              <a href="#pricing" className="text-[#9a9a98] hover:text-[#ECECEA] text-sm font-medium transition-colors">Pricing</a>
            </div>
            <div className="hidden md:flex items-center space-x-5">
              <button onClick={handleLogin} className="text-[#9a9a98] hover:text-[#ECECEA] text-sm transition-colors">Log In</button>
              <button onClick={() => handleBookDemo('navbar_icptest')} className="bg-[#d8a126] hover:bg-[#e6b23a] text-[#0e0e0f] px-5 py-2 rounded-md text-sm font-semibold transition-colors">Start ICP Test — $499</button>
            </div>
            <div className="md:hidden flex items-center space-x-3">
              <button onClick={handleLogin} className="text-[#9a9a98] hover:text-[#ECECEA] text-sm transition-colors">Log In</button>
              <button onClick={() => handleBookDemo('navbar_icptest_mobile')} className="bg-[#d8a126] text-[#0e0e0f] px-4 py-2 rounded-md text-sm font-semibold">$499</button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── SECTION 2: HERO ── */}
      <section className="relative bg-[#0e0e0f] pt-32 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#d8a126]/5 blur-[120px]"></div>
        <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#d8a126 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 border border-[#9a7420]/50 text-[#d8a126] text-[11px] font-semibold px-3 py-1.5 rounded-full mb-7 tracking-[0.15em] uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-[#d8a126]"></span>
                ICP Signal Test · 500 Cold Buyers on LinkedIn
              </div>
              <h1 className="font-serif text-4xl md:text-[3.4rem] font-semibold text-[#ECECEA] leading-[1.05] tracking-tight mb-6">
                The most expensive mistake in B2B is wasting six months on the wrong ICP.
              </h1>
              <p className="text-lg text-[#9a9a98] leading-relaxed mb-9 max-w-xl">
                Your buyers are on LinkedIn, and they'll tell you the truth—if you reach them as a peer, not a pitch. DIY gets you throttled and ignored. We run it for you and hand back a clear verdict.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2.5 mb-10">
                {['500 Managed LinkedIn Sends', 'Anonymized Peer Profiles', 'Zero Personal Account Risk'].map((metric) => (
                  <span key={metric} className="text-[#9a9a98] text-sm flex items-center gap-2">
                    <span className="w-1 h-1 bg-[#d8a126] rounded-full flex-shrink-0"></span>
                    {metric}
                  </span>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => handleBookDemo('hero_primary')} className="bg-[#d8a126] hover:bg-[#e6b23a] text-[#0e0e0f] px-8 py-4 rounded-md text-base font-semibold transition-colors">Launch Your ICP Test — $499</button>
                <button onClick={() => handleContactSales('hero_secondary')} className="border border-[#2c2c30] hover:border-[#6a6a68] text-[#9a9a98] hover:text-[#ECECEA] px-8 py-4 rounded-md text-base font-medium transition-colors">Download Sample Intelligence Report</button>
              </div>
            </div>
            <div className="hidden lg:block">
              {/* Report mockup — LIGHT / anonymized sample, floats against the dark hero */}
              <div className="bg-[#faf9f5] rounded-2xl border border-[#e3ded3] p-6 shadow-2xl shadow-black/50">
                <div className="flex items-start justify-between mb-5 pb-5 border-b border-[#e3ded3]">
                  <div>
                    <div className="text-[10px] text-[#9a7420] uppercase tracking-[0.18em] mb-1.5 font-semibold">Sample · ICP Signal Test</div>
                    <div className="text-[#1a1a1c] font-serif font-semibold text-base">New-Segment Framing Test</div>
                    <div className="text-[#8a8478] text-xs mt-1">Anonymized client · 174 total sends</div>
                  </div>
                  <span className="bg-[#5a8a52]/12 text-[#4f7a47] text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#5a8a52]/25">Published</span>
                </div>
                <div className="grid grid-cols-4 gap-2 mb-6">
                  {[{ label: 'Sent', value: '174', color: 'bg-[#e3ded3]', text: 'text-[#57534e]' }, { label: 'Connected', value: '29', color: 'bg-[#3a5a80]', text: 'text-white' }, { label: 'Replied', value: '8', color: 'bg-[#6a4f8a]', text: 'text-white' }, { label: 'Completed', value: '5', color: 'bg-[#d8a126]', text: 'text-[#0e0e0f]' }].map((stat) => (
                    <div key={stat.label} className="text-center">
                      <div className={`${stat.color} rounded-md py-2.5 mb-1.5`}><span className={`font-serif font-bold text-lg ${stat.text}`}>{stat.value}</span></div>
                      <span className="text-[#8a8478] text-[11px]">{stat.label}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-[#9a7420] uppercase tracking-[0.18em] font-semibold mb-3">Split Test Results</div>
                <div className="bg-[#f0ede5] rounded-xl p-4 border border-[#e3ded3] mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#3a3630] text-sm font-medium">V1: Credibility Hook</span>
                    <span className="text-[#8a8478] text-xs">99 sends</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-white rounded-full h-2 border border-[#e3ded3]"><div className="bg-[#a8a29a] h-2 rounded-full w-1/12"></div></div>
                    <span className="text-[#57534e] font-serif text-sm font-bold w-10 text-right">2.0%</span>
                  </div>
                  <div className="text-[#8a8478] text-[11px] mt-1.5">Completion Rate · Cost/Signal: $18.81</div>
                </div>
                <div className="bg-[#fbf3dd] rounded-xl p-4 border border-[#d8a126] relative">
                  <div className="absolute -top-3 left-4"><span className="bg-[#d8a126] text-[#0e0e0f] text-[11px] font-bold px-2.5 py-0.5 rounded-full">★ WINNER</span></div>
                  <div className="flex items-center justify-between mb-2 mt-1">
                    <span className="text-[#1a1a1c] text-sm font-semibold">V2: Founder Pain Point</span>
                    <span className="text-[#8a8478] text-xs">75 sends</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-white rounded-full h-2 border border-[#e3ded3]"><div className="bg-[#d8a126] h-2 rounded-full w-1/6"></div></div>
                    <span className="text-[#9a7420] font-serif text-sm font-bold w-10 text-right">4.0%</span>
                  </div>
                  <div className="text-[#a8862a] text-[11px] mt-1.5">Completion Rate · Cost/Signal: ~$10.00</div>
                </div>
                <div className="mt-4 pt-4 border-t border-[#e3ded3] flex items-center justify-between text-xs text-[#8a8478]">
                  <span>Signal Verdict: <span className="text-[#4f7a47] font-medium">Green — Proceed with V2</span></span>
                  <span>2× improvement</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 2.5: BUY BEFORE YOU BET ── */}
      <section className="bg-[#141416] py-20 px-4 sm:px-6 lg:px-8 border-y border-[#2c2c30]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-[11px] font-semibold text-[#9a7420] uppercase tracking-[0.18em] mb-4">When To Run It</div>
            <h2 className="font-serif text-3xl md:text-[2.5rem] font-semibold text-[#ECECEA] leading-tight max-w-3xl mx-auto">The $499 that saves you from a $50,000 mistake.</h2>
            <p className="text-lg text-[#9a9a98] mt-5 max-w-2xl mx-auto leading-relaxed">Run an ICP Signal Test before you make an expensive bet on an assumption you haven't checked.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4 max-w-3xl mx-auto">
            {[
              'Hire SDRs to chase a list you haven\'t validated',
              'Spend on paid ads targeting a persona you\'re guessing at',
              'Enter a new market or vertical',
              'Reposition your messaging on a hunch',
              'Build an outbound motion around the wrong titles',
              'Pour budget into a channel before you know it works',
            ].map((item) => (
              <div key={item} className="flex items-start gap-3.5 py-3 border-b border-[#2c2c30]">
                <svg className="w-5 h-5 text-[#d8a126] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                <span className="text-[#c9c9c7] text-[15px] leading-snug">{item}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <button onClick={() => handleBookDemo('before_you_bet_cta')} className="bg-[#d8a126] hover:bg-[#e6b23a] text-[#0e0e0f] px-8 py-4 rounded-md text-base font-semibold transition-colors">Run Your ICP Test — $499</button>
          </div>
        </div>
      </section>

      {/* ── SECTION 3: THE REVEALED MARKET REALITY ── */}
      <section id="the-evidence" className="bg-[#f6f4ef] py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-[11px] font-semibold text-[#9a7420] uppercase tracking-[0.18em] mb-4">The Evidence</div>
            <h2 className="font-serif text-3xl md:text-[2.6rem] font-semibold text-[#1a1a1c] leading-tight mb-5 max-w-4xl mx-auto">We tell you what the market actually said—including things you didn't know to ask.</h2>
            <p className="text-lg text-[#5c574e] max-w-3xl mx-auto leading-relaxed">We don't measure declared preferences on fake panel questionnaires. We track revealed behavioral signals in the wild. Look at what our loops uncovered for real teams:</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-8 border border-[#e3ded3] shadow-sm">
              <div className="inline-flex items-center bg-[#3a5a80]/10 text-[#3a5a80] text-[11px] font-semibold px-3 py-1 rounded-full mb-5 uppercase tracking-[0.1em]">The Channel Check</div>
              <p className="text-[#3a3630] leading-relaxed">A dental software team thought their messaging was broken. Standing Encore ran an outbound validation run and proved that the entire addressable US target audience on LinkedIn was exactly <strong className="text-[#1a1a1c] font-semibold">39 people</strong>—saving them a year of wasted outreach budget on the wrong channel.</p>
              <div className="mt-6 pt-6 border-t border-[#e3ded3]">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-[#3a5a80] rounded-full flex items-center justify-center"><span className="text-white text-xs font-bold">◆</span></div>
                  <span className="text-[#8a8478] text-sm">Dental-Tech SaaS · New Market Entry</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-[#e3ded3] shadow-sm">
              <div className="inline-flex items-center bg-[#6a4f8a]/10 text-[#6a4f8a] text-[11px] font-semibold px-3 py-1 rounded-full mb-5 uppercase tracking-[0.1em]">The Content Check</div>
              <p className="text-[#3a3630] leading-relaxed">An enterprise startup ran hundreds of manual browser automations, hitting a wall with 0 replies and a restricted personal account. Standing Encore ran a clean tracking loop and isolated a <strong className="text-[#1a1a1c] font-semibold">60–70% read-rate</strong>—proving that the market saw the text but actively chose to ignore it, diagnosing a content failure rather than a delivery failure.</p>
              <div className="mt-6 pt-6 border-t border-[#e3ded3]">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-[#6a4f8a] rounded-full flex items-center justify-center"><span className="text-white text-xs font-bold">◆</span></div>
                  <span className="text-[#8a8478] text-sm">Enterprise AI · New Segment</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-[#e3ded3] shadow-sm">
              <div className="inline-flex items-center bg-[#d8a126]/15 text-[#9a7420] text-[11px] font-semibold px-3 py-1 rounded-full mb-5 uppercase tracking-[0.1em]">The Optimization Check</div>
              <p className="text-[#3a3630] leading-relaxed">A B2B agency assumed institutional brand credibility would drive responses. Standing Encore ran a head-to-head split test and proved that an operational pain hook out-performed institutional credibility by <strong className="text-[#1a1a1c] font-semibold">2×</strong>, cutting their cost-per-signal entirely in half.</p>
              <div className="mt-6 pt-6 border-t border-[#e3ded3]">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-[#d8a126] rounded-full flex items-center justify-center"><span className="text-[#0e0e0f] text-xs font-bold">◆</span></div>
                  <span className="text-[#8a8478] text-sm">B2B Research Agency · Message Test</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: THE PROBLEM ── */}
      <section className="bg-[#0e0e0f] py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-[11px] font-semibold text-[#9a7420] uppercase tracking-[0.18em] mb-4">The Problem</div>
            <h2 className="font-serif text-3xl md:text-[2.6rem] font-semibold text-[#ECECEA] leading-tight max-w-4xl mx-auto">Traditional B2B customer research is slow, expensive, and a liability to your personal infrastructure.</h2>
          </div>
          <div className="space-y-5">
            <div className="bg-[#1a1a1c] rounded-2xl p-8 border border-[#2c2c30] flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-[#c0492f]/15 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-[#c0492f]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#ECECEA] mb-2">Research Panels &amp; Firms ($3,000–$20,000+)</h3>
                <p className="text-[#9a9a98] leading-relaxed">You spend thousands to speak with professional, pre-recruited box-checkers, or wait months for a static corporate PDF built on trailing data.</p>
              </div>
            </div>
            <div className="bg-[#1a1a1c] rounded-2xl p-8 border border-[#2c2c30] flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-[#c8862a]/15 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-[#c8862a]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#ECECEA] mb-2">The DIY Browser Automation Trap</h3>
                <p className="text-[#9a9a98] leading-relaxed">You attempt to script cold outreach yourself. Within 48 hours, browser extensions trigger LinkedIn's defense algorithms—your personal profile gets flagged, rate-limited, or capped at 10 messages a day.</p>
              </div>
            </div>
            <div className="bg-[#1a1a1c] rounded-2xl p-8 border border-[#d8a126]/40 flex gap-6 items-start relative overflow-hidden">
              <div className="pointer-events-none absolute -right-20 -top-20 w-64 h-64 rounded-full bg-[#d8a126]/5 blur-[80px]"></div>
              <div className="flex-shrink-0 w-12 h-12 bg-[#d8a126]/15 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-[#d8a126]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
              </div>
              <div className="relative">
                <h3 className="text-lg font-semibold text-[#d8a126] mb-2">The Standing Encore Difference</h3>
                <p className="text-[#9a9a98] leading-relaxed">Our human operators manually deploy your sequences from our own verified peer research accounts. Your domain, your sales pipeline, and your personal profile stay completely clean.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 5: 3-STEP MECHANISM ── */}
      <section id="how-it-works" className="bg-[#f6f4ef] py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-[11px] font-semibold text-[#9a7420] uppercase tracking-[0.18em] mb-4">How It Works</div>
            <h2 className="font-serif text-3xl md:text-[2.6rem] font-semibold text-[#1a1a1c] leading-tight">One test. 500 cold buyers. One clear data trail.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-10 left-[16.66%] right-[16.66%] h-px bg-[#d8b96a]/50 z-0"></div>
            <div className="relative z-10 text-center">
              <div className="w-20 h-20 bg-[#0e0e0f] rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#d8a126]/30"><span className="text-[#d8a126] font-serif font-semibold text-2xl">1</span></div>
              <h3 className="font-serif text-xl font-semibold text-[#1a1a1c] mb-3">Define Your Experiment</h3>
              <p className="text-[#5c574e] leading-relaxed">Work directly with a Standing Encore operator to strip the "sales breath" out of your script. We turn your hypothesis into an open-ended, non-threatening peer inquiry.</p>
            </div>
            <div className="relative z-10 text-center">
              <div className="w-20 h-20 bg-[#0e0e0f] rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#d8a126]/30"><span className="text-[#d8a126] font-serif font-semibold text-2xl">2</span></div>
              <h3 className="font-serif text-xl font-semibold text-[#1a1a1c] mb-3">We Run the Outreach</h3>
              <p className="text-[#5c574e] leading-relaxed">Our trained CSR team manually deploys 500 targeted sequences across LinkedIn at a compliant, natural human pace. No automated spam bots.</p>
            </div>
            <div className="relative z-10 text-center">
              <div className="w-20 h-20 bg-[#0e0e0f] rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#d8a126]/30"><span className="text-[#d8a126] font-serif font-semibold text-2xl">3</span></div>
              <h3 className="font-serif text-xl font-semibold text-[#1a1a1c] mb-3">Read the Confidence Gradient</h3>
              <p className="text-[#5c574e] leading-relaxed">We process every response, extract verbatim customer quotes, map unexpected objections, and deliver an interpretive action plan.</p>
            </div>
          </div>
          <div className="text-center mt-14">
            <button onClick={() => handleBookDemo('how_it_works_cta')} className="bg-[#0e0e0f] hover:bg-[#1a1a1c] text-[#ECECEA] px-8 py-4 rounded-md text-base font-semibold transition-colors border border-[#2c2c30]">Launch Your ICP Test — $499</button>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: SINGLE-OFFER PRICING ── */}
      <section id="pricing" className="bg-[#0e0e0f] py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-[11px] font-semibold text-[#9a7420] uppercase tracking-[0.18em] mb-4">Pricing</div>
            <h2 className="font-serif text-3xl md:text-[2.6rem] font-semibold text-[#ECECEA] leading-tight max-w-3xl mx-auto">One test. One price. One clear answer.</h2>
          </div>
          <div className="max-w-lg mx-auto">
            <div className="bg-[#1a1a1c] rounded-2xl p-10 border-2 border-[#d8a126] relative shadow-2xl shadow-[#d8a126]/[0.08]">
              <div className="text-center mb-8">
                <h3 className="text-sm font-semibold text-[#d8a126] mb-3 uppercase tracking-[0.15em]">ICP Signal Test</h3>
                <div className="font-serif text-6xl font-semibold text-[#ECECEA] leading-none">$499</div>
                <div className="text-[#6a6a68] text-sm mt-3">flat · one-time · no subscription</div>
              </div>
              <ul className="space-y-4 mb-8">
                {['500 managed LinkedIn outreach sends, delivered by our human team', 'Dedicated CSR consulting to design and draft your discovery script safely', '1 complete Intelligence Report: funnel stats, verbatim customer quotes, and a clear signal verdict on your ICP'].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[#c9c9c7] text-[15px]">
                    <svg className="w-5 h-5 text-[#d8a126] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    {item}
                  </li>
                ))}
              </ul>
              <button onClick={() => handleBookDemo('pricing_icptest')} className="w-full bg-[#d8a126] hover:bg-[#e6b23a] text-[#0e0e0f] py-4 rounded-md font-semibold text-base transition-colors">Launch Your ICP Test</button>
              <p className="text-center text-[#6a6a68] text-sm mt-5">Verdict-backed report · your data to keep</p>
            </div>
            <p className="text-center text-[#6a6a68] text-sm mt-8 leading-relaxed">
              Compare: a research panel runs <span className="text-[#9a9a98]">$3,000+</span>, and DIY outreach costs you <span className="text-[#9a9a98]">a month of your own time</span>—with your personal account on the line.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 7: CRITICAL FAQs ── */}
      <section className="bg-[#f6f4ef] py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-[11px] font-semibold text-[#9a7420] uppercase tracking-[0.18em] mb-4">FAQ</div>
            <h2 className="font-serif text-3xl md:text-[2.6rem] font-semibold text-[#1a1a1c] leading-tight">Critical Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-[#e3ded3] rounded-xl overflow-hidden bg-white">
                <button onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)} className="w-full px-6 py-5 text-left hover:bg-[#faf9f5] transition-colors flex justify-between items-center gap-4">
                  <span className="font-semibold text-[#1a1a1c] text-base">{faq.question}</span>
                  <svg className={`w-5 h-5 text-[#9a7420] flex-shrink-0 transition-transform duration-200 ${openFaqIndex === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-5 border-t border-[#e3ded3]">
                    <p className="text-[#5c574e] leading-relaxed pt-4">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 8: FINAL CTA ── */}
      <section className="relative bg-[#0e0e0f] py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(#d8a126 1px, transparent 1px)', backgroundSize: '28px 28px' }}></div>
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-40 w-[700px] h-[400px] rounded-full bg-[#d8a126]/10 blur-[120px]"></div>
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-[2.75rem] font-semibold text-[#ECECEA] leading-tight mb-5">Ready to find out who your real buyer is?</h2>
          <p className="text-lg text-[#9a9a98] mb-10 max-w-xl mx-auto">500 targeted outbound sends. 1 complete Intelligence Report with a clear signal verdict. No subscription retainers.</p>
          <button onClick={() => handleBookDemo('final_cta')} className="bg-[#d8a126] hover:bg-[#e6b23a] text-[#0e0e0f] px-10 py-4 rounded-md text-lg font-semibold transition-colors">Launch Your ICP Test</button>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#0e0e0f] border-t border-[#2c2c30] py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2.5">
            <span className="w-2 h-2 rounded-full bg-[#d8a126]"></span>
            <span className="text-[#ECECEA] font-semibold text-sm">Standing Encore</span>
          </div>
          <div className="flex items-center gap-6 text-[#6a6a68] text-sm">
            <a href="#" className="hover:text-[#9a9a98] transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-[#9a9a98] transition-colors">Terms of Service</a>
            <a href="mailto:hello@standingencore.com" className="hover:text-[#9a9a98] transition-colors">hello@standingencore.com</a>
          </div>
          <span className="text-[#4a4a48] text-sm">© 2026 Standing Encore</span>
        </div>
      </footer>

      <DemoRequestModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} source={modalSource} buttonId={modalButtonId} />
      <ContactSalesModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} source={modalSource} buttonId={modalButtonId} />

    </div>
  );
};

export default Landing;
